import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import { Box, Typography } from '@saviynt/design-system';

import type { ApiResponse } from '../../hooks/useGetDataProvider';

import DataTable from '../../../../shared/components/DataTable/DataTable';
import { useInvertedLocalize } from '../../../../shared/hooks';
import {
  type DataProvider,
  type DataTableField,
  type FieldElement,
  type PreviewDataProviderPayload,
} from '../../../../shared/types';
import { DATA_PROVIDER } from '../../../FormBuilder';
import { useGetDataProvider } from '../../hooks';

import { dataTableFieldMessages } from './messages';

interface DataTableFieldProps {
  field: DataTableField;
  dataProviders: Record<string, DataProvider>;
  canvasFields?: FieldElement[];
  customFormUid?: string;
  /** When true (public LiveForm), skip HTTP data provider API calls */
  isPublicForm?: boolean;
}

function DataTableFieldComponent({
  field,
  dataProviders,
  canvasFields = [],
  customFormUid,
  isPublicForm = false,
}: DataTableFieldProps): ReactElement {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const l = useInvertedLocalize(dataTableFieldMessages);

  const { mutate: getDataProviderPreview } = useGetDataProvider({
    customFormUid,
  });

  const dataProvider = dataProviders[field.dataProviderId];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const pageSize = field.pageSize ?? 10;

  const loadMoreData = useCallback(
    (currentPage: number) => {
      if (dataProvider.type === DATA_PROVIDER.FIXED) {
        setIsLoading(false);
        setData(
          (dataProvider.fixed as { label: string; value: string }[]).map(
            (item, index) => ({
              id: index + 1,
              label: item.label,
              value: item.value,
            })
          )
        );
        setHasMoreData(false);

        return;
      }

      if (dataProvider.type === DATA_PROVIDER.HTTP) {
        if (isPublicForm) {
          setIsLoading(false);
          setIsLoadingMore(false);
          setData([]);
          setHasMoreData(false);
          setError(null);

          return;
        }

        // Set appropriate loading state
        if (currentPage === 0) {
          setIsLoading(true);
        } else {
          setIsLoadingMore(true);
        }

        const payload: PreviewDataProviderPayload = {
          form: {
            ui: canvasFields,
            dataProviders,
            version: '1.0',
          },
          formItemId: field.id,
          page: currentPage,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          pageSize,
          searchText: '',
        };

        (
          getDataProviderPreview as (
            payload: PreviewDataProviderPayload,
            callbacks?: {
              onSuccess?: (data: ApiResponse) => void;
              onError?: (error: Error) => void;
            }
          ) => Promise<void>
        )(payload, {
          onSuccess: (response: ApiResponse) => {
            setIsLoading(false);
            setIsLoadingMore(false);

            if (response.error || response.errorMessage) {
              Logger.error(
                'Error loading data provider preview:',
                response.errorMessage
              );
              setError(new Error(response.errorMessage));

              return;
            }

            const responseData = response.data as unknown as {
              data?: Record<string, unknown>[];
              hasNext?: boolean;
            };
            const { data: responseDataArray, hasNext = false } = responseData;
            const newData = Array.isArray(responseDataArray)
              ? responseDataArray
              : [];

            if (currentPage === 0) {
              setData(newData);
            } else {
              setData((prevData) => [...prevData, ...newData]);
            }

            setHasMoreData(Boolean(hasNext));
          },
          onError: (requestError: Error) => {
            setIsLoading(false);
            setIsLoadingMore(false);
            Logger.error('Error loading data provider preview:', requestError);
            setError(
              requestError instanceof Error
                ? requestError
                : new Error('Unknown error')
            );
          },
        }).catch(() => {
          // Error is already handled by onError callback
        });
      }
    },
    [
      dataProvider,
      canvasFields,
      dataProviders,
      field.id,
      pageSize,
      getDataProviderPreview,
      isPublicForm,
    ]
  );

  useEffect(() => {
    setPage(0);
    setHasMoreData(true);
    setData([]);
    setError(null);
    loadMoreData(0);
  }, [field.id, loadMoreData]);

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;

    setPage(nextPage);
    loadMoreData(nextPage);
  }, [page, loadMoreData]);

  const columns = useMemo(() => {
    if (data.length === 0) {
      return [];
    }

    const firstRow = data[0];

    return Object.keys(firstRow).map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      isSortable: true,
      isChecked: true,
      minWidth: '150',
    }));
  }, [data]);

  const tableData = useMemo(
    () =>
      data.map((item: Record<string, unknown>, index: number) => ({
        ...item,
        key: index,
      })),
    [data]
  );

  if (isLoading) {
    return (
      <Box>
        <Typography kind='body2'>{l('Loading table data...')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography kind='body2' color='critical'>
          {l('Error loading table data:')} {error.message}
        </Typography>
      </Box>
    );
  }

  const sortOptions = [
    {
      label: 'Ascending',
      value: 'asc',
      icon: 'ArrowDown',
      shouldShowCheckIcon: true,
    },
    {
      label: 'Descending',
      value: 'desc',
      icon: 'ArrowUp',
      shouldShowCheckIcon: true,
    },
  ];

  const sortConfigs = columns.map((col) => ({ key: col.key, sortOptions }));

  return (
    <Box>
      {tableData.length > 0 ? (
        <DataTable
          data={tableData}
          columns={columns}
          searchable={field.searchable ?? true}
          sortConfigs={sortConfigs}
          tableHeight={field.tableHeight ?? 400}
          isVirtualizationEnabled={false}
          hasMoreData={hasMoreData}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMore}
        />
      ) : (
        <Box style={{ padding: '20px', textAlign: 'center' }}>
          <Typography kind='body2' color='neutral-medium'>
            {l('No data available')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default DataTableFieldComponent;
